import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine } from 'recharts';
import api from '../../services/api';
import './TailoredTAAMonitoringApp.css';

// Distinct colour per portfolio in the alignment chart; the Drummond reference in house navy.
const PORTFOLIO_COLOURS = ['#599BE3', '#91CF9B', '#e67e22', '#9b59b6', '#e74c3c', '#16a085',
  '#f39c12', '#34495e', '#c0392b', '#27ae60', '#8e44ad', '#d35400'];
const REF_COLOUR = '#072040';
const EXT_BAND_COLOUR = '#f0a500';

// Extended-band marker: a horizontal dashed segment (like the ±3% reference lines) sitting
// over an asset-class cluster, drawn only where the reference profile's tilt is beyond the base band.
const ExtBandDot = ({ cx, cy, value }) => {
  if (value == null || cx == null || cy == null) return null;
  const half = 32;
  return <line x1={cx - half} x2={cx + half} y1={cy} y2={cy}
               stroke={EXT_BAND_COLOUR} strokeWidth={2.5} strokeDasharray="5 3" />;
};

// Signed one-dp tilt (blank for missing; "0" swallows sub-0.05 noise).
const fmt = (v) => {
  if (v === null || v === undefined) return '';
  const t = Math.abs(v) < 0.05 ? '0' : v.toFixed(1);
  return v > 0 && t !== '0' ? '+' + t : t;
};
const band = (b) => (b ? `[${fmt(b.lower)}, ${fmt(b.upper)}]` : '');

// A cell's tilt with a diverging bar; colour follows compliance status.
function TiltCell({ cell, acg, overlay }) {
  const oc = overlay ? ' taa-overlay-col' : '';
  if (!cell) return <td className={`taa-cell taa-empty${oc}`} />;
  const cls = cell.status === 'ok' ? 'taa-ok'
    : cell.status === 'breach_over' ? 'taa-breach taa-over' : 'taa-breach taa-under';
  const title = `${acg}\ncurrent ${fmt(cell.current)}%  ·  SAA ${fmt(cell.neutral)}%  ·  tilt ${fmt(cell.tilt)}%\nband ${band(cell.band)}`;
  return (
    <td className={`taa-cell ${cls}${oc}`} title={title}>
      <span className="taa-tilt">{fmt(cell.tilt)}</span>
    </td>
  );
}

const BASES = [
  ['approved', 'Live'],
  ['pending', 'Pending'],
  ['draft', 'Latest draft'],
];

export default function TailoredTAAMonitoringApp() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [basis, setBasis] = useState('approved');
  const [tab, setTab] = useState('suite');
  const [alignKey, setAlignKey] = useState(null);   // selected risk profile bucket key; defaults to 70% growth

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api.get('/api/tailored-taa-monitoring/report', { params: { basis } })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [basis]);
  useEffect(() => { load(); }, [load]);

  const acgs = data?.acgs || [];

  // Alignment tab: the selected risk-profile bucket as grouped bars per ACG (one bar per
  // portfolio, plus its growth-matched Drummond reference), tilt = current − SAA.
  const alignChart = useMemo(() => {
    const ra = data?.risk_alignment;
    if (!ra || !ra.buckets?.length) return null;
    const bucket = ra.buckets.find((b) => b.key === alignKey)
      || ra.buckets.find((b) => b.growth === 70) || ra.buckets[0];
    const tol = data.base_tol || 3;
    const refLabel = bucket.reference.name;
    const extLabel = `Extended band (${bucket.reference.blender_code})`;
    const chartData = (data.acgs || []).map((a) => {
      const de = bucket.reference.tilts[a.name] ?? 0;
      const row = { acg: a.name, [refLabel]: de };
      // extended compliance boundary: only where the reference itself is tilted beyond the base band
      row[extLabel] = Math.abs(de) > tol ? de : null;
      bucket.portfolios.forEach((p) => { row[p.name] = p.tilts[a.name] ?? 0; });
      return row;
    });
    return { ra, bucket, chartData, refLabel, extLabel };
  }, [data, alignKey]);

  return (
    <div className="taa-wrap">
      <div className="taa-head">
        <h1>Tailored TAA Monitoring</h1>
        <p className="taa-muted">
          Each tailored suite's current target tilt vs its SAA, at the asset-class-group level.
          A tilt is compliant within <strong>±{data?.base_tol ?? 3}%</strong> of its own SAA, and may
          follow the tilt of the <strong>Drummond Strategic profile with the same growth exposure</strong> wherever
          that profile is more than ±{data?.base_tol ?? 3}% from its SAA (the band loosens on that side up to its tilt).
          Where no profile matches, the nearest is used, with ties going toward the middle. Red = outside the band.
        </p>
      </div>

      <div className="taa-toolbar">
        <label className="taa-field">
          <span>Target basis</span>
          <select value={basis} onChange={(e) => setBasis(e.target.value)}>
            {BASES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <button className="taa-btn" onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
        {data && (
          <span className="taa-summary">
            <span className="taa-pill taa-pill-navy">{data.summary.portfolio_count} portfolios · {data.summary.suite_count} suites</span>
            <span className={`taa-pill ${data.summary.breach_count ? 'taa-pill-red' : 'taa-pill-green'}`}>
              {data.summary.breach_count} breach{data.summary.breach_count === 1 ? '' : 'es'}
            </span>
            {data.saa_year && <span className="taa-muted">SAA FY{data.saa_year}</span>}
          </span>
        )}
      </div>

      {data && basis !== 'approved' && (
        <div className={`taa-basis-note ${basis === 'draft' ? 'taa-basis-draft' : ''}`}>
          <strong>{data.basis_label}.</strong>{' '}
          {basis === 'pending'
            ? 'Approved target weights that have not yet reached their effective date. Compliance shown as it will stand once these go live.'
            : 'Latest draft target weights — not yet approved. Indicative compliance only.'}
          <span className="taa-muted">
            {' '}References: {(data.references || []).filter((r) => r.target_set).map((r) =>
              `${r.blender_code} v${r.target_set.version} (${r.target_set.status})`
              + (r.target_set.effective_date ? ` effective ${r.target_set.effective_date}` : '')).join(' · ')}
          </span>
        </div>
      )}

      {error && <div className="taa-alert">{error}</div>}

      {data && (
        <div className="taa-tabs">
          <button className={`taa-tab ${tab === 'suite' ? 'active' : ''}`} onClick={() => setTab('suite')}>Suite compliance</button>
          <button className={`taa-tab ${tab === 'alignment' ? 'active' : ''}`} onClick={() => setTab('alignment')}>Tailored tilt alignment</button>
        </div>
      )}

      {data && tab === 'suite' && (
        <div className="taa-table-scroll">
          <table className="taa-table">
            <thead>
              <tr>
                <th className="taa-l taa-sticky">Portfolio</th>
                {acgs.map((a) => (
                  <th key={a.name} className={`taa-r ${a.overlay ? 'taa-overlay-col' : ''}`}
                      title={a.overlay ? `FX overlay · not part of the 100% · SAA band ${band(a.band)}` : `SAA band ${band(a.band)}`}>
                    <div className="taa-acg-name">{a.name}{a.overlay ? ' *' : ''}</div>
                    <div className="taa-acg-band">{band(a.band)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Reference rows: each portfolio's band follows its growth-matched profile's tilt */}
              {(data.references || []).map((r, i, arr) => (
                <tr key={r.blender_code} className={`taa-ref ${i === arr.length - 1 ? 'taa-ref-last' : ''}`}>
                  <td className="taa-l taa-sticky">
                    {r.name} <span className="taa-code">{r.blender_code}</span>
                    {i === 0 && <span className="taa-ref-tag">reference tilts</span>}
                  </td>
                  {acgs.map((a) => {
                    const c = r.cells?.[a.name];
                    return (
                      <td key={a.name} className={`taa-cell taa-refcell ${a.overlay ? 'taa-overlay-col' : ''}`} title={c ? `current ${fmt(c.current)}% · SAA ${fmt(c.neutral)}%` : ''}>
                        <span className="taa-tilt">{c ? fmt(c.tilt) : ''}</span>
                      </td>
                    );
                  })}
                </tr>
              ))}

              {(data.suites || []).map((su) => (
                <React.Fragment key={su.suite}>
                  <tr className="taa-suite">
                    <td className="taa-l taa-sticky" colSpan={1 + acgs.length}>
                      {su.suite}
                      {su.breach_count > 0 && <span className="taa-suite-breach">{su.breach_count} breach{su.breach_count === 1 ? '' : 'es'}</span>}
                    </td>
                  </tr>
                  {su.portfolios.map((p) => (
                    <tr key={p.portfolio_code} className={p.breach_count ? 'taa-row-breach' : ''}>
                      <td className="taa-l taa-sticky">
                        {p.name} <span className="taa-code">{p.portfolio_code}</span>
                        <span className="taa-ref-code" title="Reference Drummond Strategic profile for the band">ref {p.reference}</span>
                      </td>
                      {acgs.map((a) => (
                        <TiltCell key={a.name} acg={a.name} overlay={a.overlay} cell={p.cells?.[a.name]} />
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {acgs.some((a) => a.overlay) && (
            <p className="taa-foot">* Currency is an FX overlay (unhedged exposure vs the SAA currency neutral), not part of the 100% asset allocation.</p>
          )}
        </div>
      )}

      {data && tab === 'alignment' && alignChart && (
        <>
          <div className="taa-toolbar">
            <label className="taa-field">
              <span>Risk profile</span>
              <select value={alignChart.bucket.key} onChange={(e) => setAlignKey(e.target.value)}>
                {alignChart.ra.buckets.map((b) => (
                  <option key={b.key} value={b.key}>{b.label} · {b.portfolio_count} portfolio{b.portfolio_count === 1 ? '' : 's'}</option>
                ))}
              </select>
            </label>
            <span className="taa-muted">
              Each bar = a portfolio's tilt (current − SAA) per asset class; navy = {alignChart.refLabel} (the reference for this profile).
              Dashed red = ±{data.base_tol}% base band; gold dashes = the extended band ({alignChart.bucket.reference.blender_code}'s tilt)
              for asset classes where it is beyond ±{data.base_tol}%.
            </span>
          </div>
          <div className="taa-chart">
            <ResponsiveContainer width="100%" height={460}>
              <ComposedChart data={alignChart.chartData} margin={{ top: 10, right: 20, left: 4, bottom: 60 }} barCategoryGap="18%">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e7e6e6" />
                <XAxis dataKey="acg" angle={-22} textAnchor="end" interval={0} height={80} tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }}
                       label={{ value: 'Tilt vs SAA (%)', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#6c757d' } }} />
                <Tooltip formatter={(v) => (v == null ? null : `${v > 0 ? '+' : ''}${Number(v).toFixed(1)}%`)} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
                <ReferenceLine y={0} stroke="#212529" />
                <ReferenceLine y={data.base_tol} stroke="#c0392b" strokeDasharray="4 4" />
                <ReferenceLine y={-data.base_tol} stroke="#c0392b" strokeDasharray="4 4" />
                <Bar dataKey={alignChart.refLabel} fill={REF_COLOUR} maxBarSize={26} />
                {alignChart.bucket.portfolios.map((p, i) => (
                  <Bar key={p.portfolio_code} dataKey={p.name}
                       fill={PORTFOLIO_COLOURS[i % PORTFOLIO_COLOURS.length]} maxBarSize={26} />
                ))}
                <Line dataKey={alignChart.extLabel} stroke="transparent" connectNulls={false}
                      dot={<ExtBandDot />} activeDot={false} isAnimationActive={false} legendType="plainline" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {data && tab === 'suite' && (data.unassessed || []).length > 0 && (
        <div className="taa-section">
          <h2>Unassessed <span className="taa-muted">· {data.unassessed.length} · no approved target on record</span></h2>
          <div className="taa-table-scroll">
            <table className="taa-table taa-mini">
              <thead><tr><th className="taa-l">Suite</th><th className="taa-l">Portfolio</th><th className="taa-l">Code</th><th className="taa-l">Reason</th></tr></thead>
              <tbody>
                {data.unassessed.map((u) => (
                  <tr key={u.portfolio_code}><td>{u.suite}</td><td>{u.name}</td><td className="taa-code">{u.portfolio_code}</td><td>{u.reason}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
